import React from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceSyncStatusService } from './DeviceSyncStatusService';
import { DeviceSyncStatusOverview } from '../models/device-sync-status';
import { extractApiErrorMessage, notifications } from '../utils';

interface UseDeviceSyncStatusResult {
  overview: DeviceSyncStatusOverview | null;
  isBusy: boolean;
  /** True once a fetch has settled, successfully or not - lets callers tell "empty" from "not asked yet". */
  loaded: boolean;
  reload: () => void;
}

/**
 * Loads the farm's unsent device backlog.
 *
 * `farmId` picks the source: omitted, the signed-in business unit; set, another farm, which is how the
 * admin farms overview opens a farm without switching the session onto it.
 *
 * There is no polling. A phone reports on its own schedule and the numbers move in minutes, not seconds, so
 * a timer here would mostly redraw the same table - the card carries a reload control instead.
 */
export const useDeviceSyncStatus = (farmId?: string): UseDeviceSyncStatusResult => {
  const { t } = useTranslation();
  const [overview, setOverview] = React.useState<DeviceSyncStatusOverview | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    const service = new DeviceSyncStatusService();
    // The farm can change while a request is in flight; ignore anything but the newest one.
    let cancelled = false;

    setIsBusy(true);

    service
      .getOverview(farmId)
      .then((result) => {
        if (cancelled) return;
        setOverview(result);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setOverview(null);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setIsBusy(false);
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [farmId, reloadToken]);

  const reload = React.useCallback(() => setReloadToken((token) => token + 1), []);

  return { overview, isBusy, loaded, reload };
};

export default useDeviceSyncStatus;
